import { createContext, useEffect, useState } from 'react';

import {
  clearParentAuth,
  getParentAuthState,
  getParentRefreshToken,
  saveParentAuth,
} from '../utils/tokenStorage';
import { getFullName, getRoleLabel, isParentRole, isStudentRole, normalizeRole } from '../utils/auth';
import { loginParent, logoutParent, registerParent } from '../services/parentAuthService';

export const ParentAuthContext = createContext();

const syncParentAuth = () => {
  const auth = getParentAuthState();

  return {
    user: auth.user,
    accessToken: auth.accessToken,
    refreshToken: auth.refreshToken,
    isAuthenticated: !!auth.accessToken,
  };
};

export const ParentAuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => syncParentAuth());

  useEffect(() => {
    const handleAuthChange = () => setAuthState(syncParentAuth());

    window.addEventListener('parent-auth-changed', handleAuthChange);

    return () => window.removeEventListener('parent-auth-changed', handleAuthChange);
  }, []);

  const persistAuth = (data) => {
    saveParentAuth(data);
    setAuthState(syncParentAuth());
  };

  const login = async (data) => {
    const res = await loginParent(data);
    const role = normalizeRole(res?.user?.role);
    if (![3, 4].includes(role)) {
      throw new Error('Invalid parent/student role');
    }
    if (res?.access_token || res?.refresh_token || res?.user) {
      persistAuth({ ...res, user: { ...res.user, role } });
    }
    return res;
  };

  const register = async (data) => {
    const res = await registerParent(data);
    const role = normalizeRole(res?.user?.role);
    if (res?.user && ![3, 4].includes(role)) {
      throw new Error('Invalid parent/student role');
    }
    if (res?.access_token || res?.refresh_token || res?.user) {
      persistAuth(res?.user ? { ...res, user: { ...res.user, role } } : res);
    }
    return res;
  };

  const logout = async () => {
    try {
      const refreshToken = getParentRefreshToken();
      if (refreshToken) {
        await logoutParent(refreshToken);
      }
    } finally {
      clearParentAuth();
      setAuthState(syncParentAuth());
    }
  };

  return (
    <ParentAuthContext.Provider
      value={{
        ...authState,
        role: normalizeRole(authState.user?.role),
        roleLabel: getRoleLabel(authState.user?.role),
        fullName: getFullName(authState.user),
        isParent: isParentRole(authState.user?.role),
        isStudent: isStudentRole(authState.user?.role),
        login,
        register,
        logout,
        setParentAuth: persistAuth,
      }}
    >
      {children}
    </ParentAuthContext.Provider>
  );
};
